import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Layers,
  Search,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  History,
  XCircle,
  Clock,
  FileSpreadsheet,
  RefreshCw
} from "lucide-react";
// import { format } from "date-fns";
import Loader from "../../loader/Loader";
import AddProductBOM from "./AddProductBOM";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Pagination from "../Pagination";
import { domain, domainAccounting } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";
import BulkBOMproductpage from "../ProductBulkUpload/BulkBOMproductpage";
import noImage from "../../../Images/noImage.jpg";

// const formatDate = (d) => (d ? format(new Date(d), "MMM dd, yyyy") : "-");

const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [auditItem, setAuditItem] = useState(null);
  const perPage = 5;

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${domain}/api/ProductBom/history?search=${search}`,
      );
      setData(res.data);
    } catch {
      toast.error("Failed load history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) fetchHistory();
  }, [isOpen, fetchHistory]);
  if (!isOpen) return null;

  const paginated = data.slice((page - 1) * perPage, page * perPage);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-bold flex gap-2">
            <div className="p-2 bg-red-50 rounded-full">
              <Trash2 size={18} />
            </div>
            Deleted BOMs
          </h2>
          <button onClick={onClose}>
            <XCircle size={22} />
          </button>
        </div>
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="w-full pl-9 pr-4 py-2 border rounded-xl"
              placeholder="Search by output product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <Loader />
          ) : data.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              No deleted BOMs
            </div>
          ) : (
            <div className="space-y-2">
              {paginated.map((item) => (
                <div
                  key={item.id}
                  className="bg-white p-4 rounded-xl border shadow-sm flex justify-between items-center"
                >
                  <div>
                    <p className="font-semibold">{item.bomName}</p>
                    <p className="text-xs text-slate-500">
                      {item.description || "No description"}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setAuditItem(item)}
                      className="text-amber-500 hover:text-amber-700"
                    >
                      <Clock size={16} />
                    </button>
                    <button
                      onClick={async () => {
                        await onRestore(item.id);
                        fetchHistory();
                      }}
                      className="text-green-600 flex gap-1 items-center text-sm"
                    >
                      <RotateCcw size={14} /> Restore
                    </button>
                  </div>
                </div>
              ))}
              <Pagination
                itemsPerPage={perPage}
                totalItems={data.length}
                currentPage={page}
                paginate={setPage}
              />
            </div>
          )}
        </div>
      </div>
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.bomName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

const AllProductBOM = () => {
  const currentUserID = useSelector(selectUserID);
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editingBom, setEditingBom] = useState(null);
  const [auditItem, setAuditItem] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const perPage = 10;

  const fetchBoms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/ProductBom/all`);
      setBoms(res.data);
    } catch {
      toast.error("Failed load BOMs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoms();
  }, [fetchBoms]);

  useEffect(() => {
    const lower = search.toLowerCase();
    setFiltered(
      boms.filter(
        (b) =>
          b.bomName?.toLowerCase().includes(lower) ||
          b.description?.toLowerCase().includes(lower),
      ),
    );
    setPage(1);
  }, [search, boms]);

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Move this BOM to history?")) return;
    try {
      await axios.put(`${domain}/api/ProductBom/soft-delete/${id}`, {
        userID: currentUserID,
      });
      toast.success("BOM archived");
      fetchBoms();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/ProductBom/restore/${id}`, {
        userID: currentUserID,
      });
      toast.success("BOM restored");
      fetchBoms();
    } catch {
      toast.error("Restore failed");
    }
  };

  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const getAllItemsToSync = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}/api/Products/products-to-sync`);

      const bomProductIds = boms?.map((i) => i.bomProductId) ?? [];

      console.log({bomProductIds})
      console.log({result: res.data})

      const data = res.data
        ?.filter((i) => bomProductIds.includes(i.id))
        ?.map((item) => ({
          ...item,
          productImage: noImage,
        }));

      return data ?? [];
    } catch (error) {
      console.error("Error fetching products to sync:", error);
      return [];
    }
  }, [boms]);

  const handleSyncToAccounting = async () => {
    try {
      setIsSyncing(true);

      const _productData = await getAllItemsToSync();

      if (!_productData || _productData.length === 0) {
        toast.error("No items available to sync.");
        return;
      }

      const payload = _productData.map((c) => {
        return {
          itemCode: c.itemCode || "",
          itemDescription: c.productName || "",
          uomName: c.baseUomCode || "",
          purchaseUomName: c.purchaseUomCode || "",
          salesUomName: c.salesUomCode || "",
          brandName: c.brandName || "",
          categoryName: c.categoryName || "",
          isBOM: c.isBom || false,
          itemComponents: c.bomComponents.map((comp) => ({
            componentItemCode: comp.componentItemCode || "",
            uomName: comp.uomName || "",
            quantity: comp.quantity || 0,
          })),
        };
      });

      const endpoint = `${domainAccounting}/api/Item/bulk`;

      await axios.post(endpoint, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        `Successfully synced ${payload.length} products to Accounting!`,
      );
      setIsSyncModalVisible(false);
    } catch (error) {
      console.error("Accounting Sync Error:", error);
      toast.error("Failed to sync products to Accounting.");
    } finally {
      setIsSyncing(false);
    }
  };

  const currentItems = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="min-h-screen pb-20">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex gap-2">
              <Layers className="text-purple-600" /> Bill of Materials (BOM)
            </h1>
            <p className="text-sm text-slate-500">
              Manage product recipes & bundles
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setHistoryOpen(true)}
              className="flex gap-2 bg-slate-50 border px-4 py-2 rounded-xl"
            >
              <History size={18} /> History
            </button>
            <button
              onClick={() => setIsBulkUploadOpen(true)}
              className="flex items-center gap-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 px-4 py-2 rounded-xl shadow-sm transition"
            >
              <FileSpreadsheet size={15} />
              <span className="hidden sm:inline">Bulk Upload</span>
            </button>
            <button
                onClick={() => setIsSyncModalVisible(true)}
                className="flex items-center gap-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 px-4 py-2 rounded-xl shadow-sm transition"
              >
                <RefreshCw size={18} />
                <span>Sync to Accounting</span>
              </button>
            <button
              onClick={() => {
                setEditingBom(null);
                setModalOpen(true);
              }}
              className="flex gap-2 bg-purple-600 text-white px-5 py-2 rounded-xl shadow"
            >
              <Plus size={18} /> Add BOM
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border flex items-center gap-4">
            <div className="p-3 bg-purple-50 rounded-xl">
              <Layers size={24} />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total BOMs</p>
              <p className="text-2xl font-bold">{filtered.length}</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="w-full pl-10 pr-4 py-2 border rounded-xl"
                placeholder="Search by output product or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left">Output Product</th>
                    <th className="px-6 py-4 text-left">Description</th>
                    <th className="px-6 py-4 text-center">Components</th>
                    <th className="px-6 py-4 text-center">Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((bom) => (
                    <tr key={bom.id} className="border-t hover:bg-slate-50">
                      <td className="px-6 py-4 font-semibold">{bom.bomName}</td>
                      <td className="px-6 py-4 text-slate-500">
                        {bom.description || "-"}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {bom.componentCount}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {bom.isActive ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right flex justify-end gap-2">
                        <button
                          onClick={() => setAuditItem(bom)}
                          className="p-2 text-slate-400 hover:text-amber-600"
                        >
                          <Clock size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setEditingBom(bom);
                            setModalOpen(true);
                          }}
                          className="p-2 text-slate-400 hover:text-indigo-600"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleSoftDelete(bom.id)}
                          className="p-2 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-3">
              {currentItems.map((bom) => (
                <div
                  key={bom.id}
                  className="bg-white p-4 rounded-xl border shadow-sm"
                >
                  <div className="flex justify-between">
                    <div>
                      <p className="font-bold">{bom.bomName}</p>
                      <p className="text-sm text-slate-500">
                        {bom.description}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setAuditItem(bom)}>
                        <Clock size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingBom(bom);
                          setModalOpen(true);
                        }}
                      >
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleSoftDelete(bom.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-3 text-xs items-center">
                    <span className="bg-slate-100 px-2 py-1 rounded">
                      Components: {bom.componentCount}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full ${
                        bom.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {bom.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Pagination
                itemsPerPage={perPage}
                totalItems={filtered.length}
                currentPage={page}
                paginate={setPage}
              />
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <AddProductBOM
          onClose={() => {
            setModalOpen(false);
            fetchBoms();
          }}
          refreshData={fetchBoms}
          bomToEdit={editingBom}
        />
      )}
      <DeletedHistoryModal
        isOpen={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          fetchBoms();
        }}
        onRestore={handleRestore}
      />
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.bomName}
        rows={buildAuditRows(auditItem)}
      />

      {/* ── BULK UPLOAD ── */}
      {isBulkUploadOpen && (
        <BulkBOMproductpage
          isOpen={isBulkUploadOpen}
          initialRestoreId={null}
          onClose={() => setIsBulkUploadOpen(false)}
          onSuccess={() => {
            setIsBulkUploadOpen(false);
            fetchBoms();
            toast.success("Bulk upload complete — list refreshed.");
          }}
        />
      )}

      <SyncConfirmModal
        isOpen={isSyncModalVisible}
        onClose={() => setIsSyncModalVisible(false)}
        onConfirm={handleSyncToAccounting}
        dataCount={boms.length}
        syncing={isSyncing}
      />
    </div>
  );
};

// ==========================================
// SYNC CONFIRMATION MODAL
// ==========================================
const SyncConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  dataCount,
  syncing,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col transform transition-all">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-emerald-50 rounded-full text-emerald-600">
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
            </div>
            Sync to Accounting
          </h2>
          <button
            onClick={onClose}
            disabled={syncing}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition disabled:opacity-50"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 bg-slate-50/50">
          <div className="bg-white p-4 rounded-xl border border-slate-200 mb-2">
            <p className="text-sm text-slate-600 leading-relaxed text-center">
              Are you sure you want to sync <br />
              <strong className="text-xl text-emerald-600">
                {dataCount}
              </strong>{" "}
              <br />
              products to the Accounting system?
            </p>
            <p className="text-xs text-slate-400 text-center mt-3">
              This will securely push all current product data to the
              centralized accounting database.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-5">
            <button
              onClick={onClose}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={syncing}
              className="px-4 py-2 flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-70"
            >
              {syncing ? (
                <>
                  <RefreshCw size={16} className="animate-spin" /> Syncing...
                </>
              ) : (
                "Confirm Sync"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllProductBOM;
